import { useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'

import useRegionsListQuery from 'common/api/queries/useRegionsListQuery'
import ConfirmationModal from 'common/components/confirmationModal'
import Drawer from 'common/components/drawer'
import useDisablePointerEventsWhenModalVisible from 'common/components/drawer/useDisablePointerEventsWhenModalVisible'
import { useAppSelector } from 'common/hooks/redux'
import { LanguageRule } from 'features/admin/language/api/api'
import useCreateLanguageRuleMutation from 'features/admin/language/api/mutations/useCreateLanguageRuleMutation'

import CancelButton from './CancelButton'
import FormContent from './FormContent'
import SubmitButton from './SubmitButton'

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  languageRules: LanguageRule[]
}

const AddLanguageRulesDrawer = ({
  isOpen,
  onOpenChange,
  languageRules,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const { data: regions } = useRegionsListQuery()

  const { mutate, isPending: isLoading } = useCreateLanguageRuleMutation()
  const [isFormDirty, setFormDirty] = useState<boolean>(false)
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false)

  const languageRulesCountByRegion = useMemo(() => {
    const countMap: Record<string, number> = {}
    languageRules.forEach((rule) => {
      if (rule.isoLanguage === 'all') {
        countMap['all'] = (countMap['all'] || 0) + 1
      } else {
        const region = regions?.find(
          (region) =>
            region.language_settings?.iso_language === rule.isoLanguage
        )
        const regionId = region?.id || rule.isoLanguage
        countMap[regionId] = (countMap[regionId] || 0) + 1
      }
    })
    return countMap
  }, [languageRules, regions])

  const handleSave = (values) => {
    const regionValue = values.region.value
    const selectedIsoLanguage =
      regionValue === 'all'
        ? 'all'
        : regions?.find((region) => region.id === regionValue)
            ?.language_settings?.iso_language

    if (!selectedIsoLanguage) {
      return
    }

    const payload = {
      rule: values.rule,
      examples: values.examples.map((example) => ({
        exampleInput: example.exampleInput,
        exampleOutput: example.exampleOutput,
      })),
      isoLanguage: selectedIsoLanguage,
    }

    mutate(
      { accountId, data: payload },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  useDisablePointerEventsWhenModalVisible(isConfirmationVisible)

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title="Create language rule"
        dismissible={!isConfirmationVisible}
        onOpenChange={onOpenChange}
        content={
          <div className="w-219 flex flex-col h-full">
            <Form
              onSubmit={handleSave}
              render={({ handleSubmit }) => {
                return (
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col flex-1 h-full"
                  >
                    <FormSpy
                      onChange={(props) => {
                        const dirtyFields = Object.keys(props.dirtyFields)

                        setFormDirty(dirtyFields.length > 0)
                      }}
                    />

                    <FormContent
                      title="Create language rule"
                      description="Create a new language-specific rule to ensure grammatically accurate and fluent output."
                      languageRulesCountByRegion={languageRulesCountByRegion}
                    />

                    <div className="flex flex-none justify-end mt-auto w-full py-6 border-t border-gold-400 gap-3">
                      <CancelButton
                        data-testid="language-rule-cancel-button"
                        onClick={() => {
                          if (isFormDirty) {
                            setIsConfirmationVisible(true)
                          } else {
                            onOpenChange(false)
                          }
                        }}
                      />
                      <SubmitButton
                        data-testid="language-rule-submit-button"
                        isLoading={isLoading}
                      >
                        Done
                      </SubmitButton>
                    </div>
                  </form>
                )
              }}
            />
          </div>
        }
      />
      <ConfirmationModal
        title="Are you sure you want to leave without saving?"
        confirmationText="Any unsaved instructions and examples will be lost."
        confirmButtonText="Yes, I want to leave"
        cancelButtonText="Go back"
        open={isConfirmationVisible}
        hasConfirmationSlider={false}
        onConfirm={() => {
          onOpenChange(false)
          setIsConfirmationVisible(false)
        }}
        onCancel={() => {
          setIsConfirmationVisible(false)
        }}
      />
    </>
  )
}

export default AddLanguageRulesDrawer
